import { useState, useEffect } from 'react';
import supabase, { isSupabaseConfigured } from '../lib/supabase';
import { useAuthStore } from '../store/authStore';
import type { Database } from '../types/database.types';

type Tables = Database['public']['Tables'];

// Generic hook for fetching data
export function useQuery<T>(
  query: () => Promise<{ data: T | null; error: any }>,
  dependencies: any[] = []
) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Bumped by refetch() to force the fetch effect to re-run independently
  // of the user-supplied dependency list. Used by the realtime listeners.
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      // If Supabase is not configured, return empty data immediately
      if (!isSupabaseConfigured) {
        setIsLoading(false);
        setData(null);
        setError(null);
        return;
      }

      try {
        setIsLoading(true);
        const { data, error } = await query();

        if (cancelled) return;

        if (error) throw error;
        setData(data);
        setError(null);
      } catch (err: any) {
        if (!cancelled) {
          setError(err);
          setData(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [...dependencies, version]);

  return { data, error, isLoading, refetch: () => setVersion((v) => v + 1) };
}

/**
 * Subscribe to Postgres-level row changes on one or more tables and
 * fire `onChange` when any matching event lands. Used to refetch a
 * stale availability view as soon as a booking, schedule change, or
 * closure hits the database.
 *
 * Filter strings follow Supabase Realtime's tiny grammar — currently
 * only `column=eq.value` is supported per subscription. Pass null /
 * undefined `id` to skip wiring the channel at all (e.g. while a
 * parent is still loading the entity).
 */
export function useRealtimeRefresh(
  channel: string,
  subscriptions: Array<{ table: string; filter?: string }>,
  onChange: () => void,
  enabled: boolean = true,
) {
  useEffect(() => {
    if (!enabled || !isSupabaseConfigured) return;
    const ch = supabase.channel(channel);
    for (const s of subscriptions) {
      // postgres_changes is typed against the generated Database union;
      // our table list is broader so cast via any.
      (ch as any).on(
        'postgres_changes',
        { event: '*', schema: 'public', table: s.table, ...(s.filter ? { filter: s.filter } : {}) },
        () => onChange(),
      );
    }
    ch.subscribe();
    return () => { supabase.removeChannel(ch); };
    // Note: we intentionally serialize the subscription list via
    // JSON.stringify so a caller's recomputed array doesn't tear the
    // channel down on every render.
  }, [channel, JSON.stringify(subscriptions), enabled]);
}

// Machines API
export function useMachines() {
  return useQuery(async () => {
    return supabase
      .from('machines')
      .select('*, machine_specs(*)')
      .eq('status', 'available');
  });
}

export function useMachine(id: string) {
  return useQuery(
    async () => {
      return supabase
        .from('machines')
        .select('*, machine_specs(*), machine_images(*)')
        .eq('id', id)
        .single();
    },
    [id]
  );
}

// Rental Packages API
export function useRentalPackages(machineId?: string) {
  return useQuery(async () => {
    let query = supabase.from('rental_packages').select('*').eq('is_active', true);

    if (machineId) {
      query = query.eq('machine_id', machineId);
    }

    return query.order('sort_order');
  }, [machineId]);
}

// Delivery Zones API
export function useDeliveryZones() {
  return useQuery(async () => {
    return supabase
      .from('delivery_zones')
      .select('*')
      .eq('is_active', true)
      .order('name');
  });
}

// Pickup Locations API
export function usePickupLocations() {
  return useQuery(async () => {
    return supabase
      .from('pickup_locations')
      .select('*')
      .eq('is_active', true)
      .order('name');
  });
}

// Clubs / Courts API --------------------------------------------------------
export type CourtFilters = {
  surface?: 'hard' | 'clay' | 'grass' | 'artificial' | 'carpet';
  indoor?: boolean;
  lights?: boolean;
};

export function useClubs() {
  return useQuery(async () => {
    return supabase
      .from('clubs')
      .select('id, name, slug, address, description, photos, logo_url, amenities, coordinates, accepts_freelance_coaches')
      .eq('is_active', true)
      .order('name');
  });
}

/**
 * Flat list of every active venue with its parent club joined. One row
 * per venue — multi-venue clubs surface as several rows with the same
 * club_id. Used by ClubsScreen to render per-venue cards grouped by
 * brand. brand_avg_rating / brand_review_count come from the
 * club_brand_ratings view added in PR 1.
 */
export function useVenues() {
  return useQuery<Array<any>>(async () => {
    // club_venues + club_brand_ratings aren't in the generated types
    // yet; cast the table accessors to bypass the union.
    const { data: venues, error } = await (supabase.from as any)('club_venues')
      .select(`
        id, name, slug, address, coordinates, photos, is_primary, is_active,
        club_id,
        clubs!inner(id, name, slug, logo_url, accepts_freelance_coaches, is_active)
      `)
      .eq('is_active', true)
      .eq('clubs.is_active', true);
    if (error) return { data: null, error } as any;

    const venueIds = ((venues ?? []) as any[]).map((v) => v.id);
    const clubIds = Array.from(new Set(((venues ?? []) as any[]).map((v) => v.club_id)));

    // Fetch ratings + courts in parallel. Courts give us the
    // "from AED X" badge on each card and the available-surfaces
    // set used by the surface-filter chips.
    const [ratingsRes, courtsRes] = await Promise.all([
      clubIds.length > 0
        ? (supabase.from as any)('club_brand_ratings')
            .select('club_id, avg_rating, review_count')
            .in('club_id', clubIds)
        : Promise.resolve({ data: [] as any[] }),
      venueIds.length > 0
        // Cast through `any` because the courts/venue_id IN-clause
        // tickles TS2589 "Type instantiation is excessively deep" on
        // PostgREST's generated types.
        ? (supabase.from as any)('courts')
            .select('venue_id, surface, hourly_rate_offpeak')
            .in('venue_id', venueIds)
            .eq('is_active', true)
        : Promise.resolve({ data: [] as any[] }),
    ]);

    const ratings: Record<string, { avg: number; count: number }> = {};
    for (const row of (ratingsRes.data ?? []) as Array<{ club_id: string; avg_rating: number; review_count: number }>) {
      ratings[row.club_id] = { avg: Number(row.avg_rating ?? 0), count: Number(row.review_count ?? 0) };
    }

    const courtSummary: Record<string, { minPrice: number | null; surfaces: string[] }> = {};
    for (const row of (courtsRes.data ?? []) as Array<{ venue_id: string; surface: string | null; hourly_rate_offpeak: number }>) {
      const entry = courtSummary[row.venue_id] ?? { minPrice: null, surfaces: [] as string[] };
      const price = Number(row.hourly_rate_offpeak);
      if (Number.isFinite(price) && (entry.minPrice === null || price < entry.minPrice)) {
        entry.minPrice = price;
      }
      if (row.surface && !entry.surfaces.includes(row.surface)) entry.surfaces.push(row.surface);
      courtSummary[row.venue_id] = entry;
    }

    const enriched = ((venues ?? []) as any[]).map((v) => ({
      ...v,
      brand_avg_rating: ratings[v.club_id]?.avg ?? 0,
      brand_review_count: ratings[v.club_id]?.count ?? 0,
      min_price: courtSummary[v.id]?.minPrice ?? null,
      surfaces: courtSummary[v.id]?.surfaces ?? [],
    }));
    return { data: enriched, error: null };
  });
}

/** Full club context: clubs row + courts + eligible coaches + hours. */
export function useClub(id: string | undefined) {
  return useQuery(async () => {
    if (!id) return { data: null, error: null };
    const [club, courts, staff, open, affiliated, venues, packages, promos, rating] = await Promise.all([
      supabase.from('clubs')
        .select('id, name, slug, address, description, photos, logo_url, amenities, coordinates, contact_email, contact_phone, accepts_freelance_coaches')
        .eq('id', id).eq('is_active', true).single(),
      supabase.from('courts')
        .select('id, name, surface, is_indoor, has_lights, hourly_rate_offpeak, hourly_rate_peak, currency, slot_minutes, photos, venue_id')
        .eq('club_id', id).eq('is_active', true).order('name'),
      supabase.from('coaches')
        .select('id, display_name, bio, photo_url, type, specialties, languages, years_experience')
        .eq('home_club_id', id).eq('type', 'staff').eq('is_active', true).eq('verification_status', 'verified').order('display_name'),
      supabase.from('club_hours').select('day_of_week, open_time, close_time').eq('club_id', id),
      supabase.from('coach_affiliations')
        .select('status, coaches!inner(id, display_name, bio, photo_url, type, specialties, languages, years_experience, is_active, verification_status)')
        .eq('club_id', id).eq('status', 'approved'),
      // club_venues isn't in the generated database.types yet — regenerate
      // is a separate chore, so cast the `from` to bypass the narrow union.
      // venue_hours is selected as a relationship so the location list
      // can show today's hours per venue without a second round-trip.
      (supabase.from as any)('club_venues')
        .select('id, name, slug, address, coordinates, photos, is_primary, venue_hours(day_of_week, open_time, close_time)')
        .eq('club_id', id).eq('is_active', true)
        .order('is_primary', { ascending: false })
        .order('name', { ascending: true }),
      // club_packages: brand-level offerings (group classes, private
      // bundles, clinics). Customer page renders these as cards.
      (supabase.from as any)('club_packages')
        .select('id, name, package_type, description, schedule_text, duration_minutes, num_sessions, per_session_price, total_price, currency, level_label, audience, display_order')
        .eq('club_id', id).eq('is_active', true)
        .order('display_order', { ascending: true })
        .order('total_price', { ascending: true }),
      // Public promotions for this brand (brand-wide club_id match,
      // OR venue-scoped against any of this club's venues — handled
      // client-side via the venues IN clause once we have the list).
      // For the simple brand-page case, brand-scoped promos cover
      // most of what customers see.
      (supabase.from as any)('promo_codes')
        .select('id, code, display_label, description, discount_type, discount_value, valid_until, club_id, venue_id')
        .eq('is_public', true)
        .eq('is_active', true)
        .or(`club_id.eq.${id},and(club_id.is.null,venue_id.is.null)`),
      // club_avg_rating isn't in the generated rpc-name union —
      // skip the type narrowing the same way we do for club_venues.
      (supabase.rpc as any)('club_avg_rating', { p_club_id: id }),
    ]);
    if (club.error) return { data: null, error: club.error };
    const freelance = ((affiliated.data ?? []) as any[])
      .map((a: any) => a.coaches)
      .filter((c: any) => c && c.is_active && c.verification_status === 'verified' && c.type === 'freelance');
    return {
      data: {
        club: club.data,
        courts: courts.data ?? [],
        staffCoaches: staff.data ?? [],
        freelanceCoaches: freelance,
        hours: open.data ?? [],
        venues: venues.data ?? [],
        packages: packages.data ?? [],
        promos: promos.data ?? [],
        avgRating: (rating.data as unknown as number | null) ?? null,
      },
      error: null,
    };
  }, [id]);
}

export function useCourts(filters: CourtFilters = {}) {
  const deps = [filters.surface, filters.indoor, filters.lights];
  return useQuery(async () => {
    // !inner forces an INNER JOIN against clubs so courts at hidden
    // (is_active=false) clubs are excluded. Otherwise the relationship
    // defaults to LEFT JOIN and such courts leak through here but are
    // missing on the Clubs list.
    let q = supabase
      .from('courts')
      .select(`
        id, name, surface, is_indoor, has_lights,
        hourly_rate_offpeak, hourly_rate_peak, currency, slot_minutes,
        photos, club_id,
        clubs!inner(id, name, address, photos, is_active)
      `)
      .eq('is_active', true)
      .eq('clubs.is_active', true);
    if (filters.surface) q = q.eq('surface', filters.surface);
    if (filters.indoor !== undefined) q = q.eq('is_indoor', filters.indoor);
    if (filters.lights !== undefined) q = q.eq('has_lights', filters.lights);
    return q.order('hourly_rate_offpeak');
  }, deps);
}

export function useCourt(id: string) {
  const q = useQuery(async () => {
    return supabase
      .from('courts')
      .select(`
        *,
        clubs(id, name, address, description, photos, amenities, contact_phone)
      `)
      .eq('id', id)
      .single();
  }, [id]);

  // Live-refresh on bookings for this court. Hours + closures live on
  // the parent club; useClubHours subscribes to those, so each consumer
  // of court availability gets covered by the union of these channels.
  useRealtimeRefresh(
    `court-${id}`,
    [{ table: 'court_bookings', filter: `court_id=eq.${id}` }],
    q.refetch,
    !!id,
  );

  return q;
}

export function useCourtBookings(userId?: string) {
  return useQuery(async () => {
    const uid = userId || useAuthStore.getState().user?.id;
    if (!uid) return { data: null, error: null };
    return supabase
      .from('court_bookings')
      .select(`
        id, booking_reference, start_at, end_at, status, total_amount, currency,
        courts(id, name, surface, photos),
        clubs(id, name, address)
      `)
      .eq('user_id', uid)
      .order('start_at', { ascending: false });
  }, [userId]);
}

export function useCreateCourtBooking() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createCourtBooking = async (payload: {
    courtId: string;
    startAt: string;
    endAt: string;
    notes?: string;
    paymentMethod?: 'card' | 'apple_pay';
    rentals?: Array<{ rentalItemId: string; quantity: number }>;
    promoCode?: string;
  }) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await invokeFunction<any>('create-court-booking', payload);
      setIsLoading(false);
      return { data, error: null };
    } catch (err: any) {
      setError(err);
      setIsLoading(false);
      return { data: null, error: err };
    }
  };

  return { createCourtBooking, isLoading, error };
}

export async function validateCourtAvailability(payload: {
  courtId: string;
  startAt: string;
  endAt: string;
}) {
  const { data, error } = await supabase.functions.invoke('validate-court-availability', {
    body: payload,
  });
  if (error) throw error;
  return data as { available: boolean; reason?: string };
}

// Promo codes -------------------------------------------------------------
export type PromoValidation = {
  ok: true;
  code: string;
  promoCodeId: string;
  description: string | null;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  discount: number;
  finalTotal: number;
};

export async function validatePromoCode(code: string, subtotal: number): Promise<
  PromoValidation | { ok: false; error: string }
> {
  const { data, error } = await supabase.functions.invoke('validate-promo-code', {
    body: { code, subtotal },
  });
  if (error) {
    const parsed = await normalizeFunctionError(error, 'Could not check code');
    return { ok: false, error: parsed.message };
  }
  return data as PromoValidation | { ok: false; error: string };
}

// Coaches API -------------------------------------------------------------
export type CoachFilters = {
  lessonType?: 'private' | 'semi_private' | 'group';
  environment?: 'indoor' | 'outdoor';
};

export function useCoaches(filters: CoachFilters = {}) {
  const deps = [filters.lessonType, filters.environment];
  return useQuery(async () => {
    // Pull active+verified coaches plus their rate cards in one
    // round-trip so the list-view can show a "from AED X" price and
    // filter by price band without a per-row fetch.
    const { data, error } = await supabase
      .from('coaches')
      .select(`
        id, display_name, bio, photo_url, type, home_club_id,
        specialties, languages, years_experience, gender, nationality,
        clubs:home_club_id(id, name, address),
        coach_lesson_types(lesson_type, environment, per_person_price, currency, is_active)
      `)
      .eq('is_active', true)
      .eq('verification_status', 'verified')
      .order('display_name');
    if (error) return { data: null, error } as any;

    // Compute min active rate per coach so the list card can render
    // "From AED N". Filtering by lessonType/environment narrows the
    // pool of rates considered; for the default "Any" it's the
    // overall minimum across this coach's rate card.
    const enriched = ((data ?? []) as any[]).map((c) => {
      const rates = ((c.coach_lesson_types ?? []) as any[]).filter((r) => r.is_active);
      const filteredRates = rates.filter((r) => {
        if (filters.lessonType && r.lesson_type !== filters.lessonType) return false;
        if (filters.environment && r.environment !== filters.environment) return false;
        return true;
      });
      const prices = filteredRates.map((r) => Number(r.per_person_price)).filter(Number.isFinite);
      const minPrice = prices.length > 0 ? Math.min(...prices) : null;
      return { ...c, min_price: minPrice };
    });
    return { data: enriched, error: null };
  }, deps);
}

export function useCoach(id: string) {
  const q = useQuery(async () => {
    return supabase
      .from('coaches')
      .select(`
        *,
        clubs:home_club_id(id, name, address, photos),
        coach_lesson_types(id, lesson_type, environment, session_minutes, per_person_price, currency, max_participants, is_active)
      `)
      .eq('id', id)
      .single();
  }, [id]);

  // Live-refresh whenever this coach's bookings, hours, or time-off
  // changes. Lesson rates are mutated rarely; skipped to keep the
  // channel scoped to availability-relevant tables.
  useRealtimeRefresh(
    `coach-${id}`,
    [
      { table: 'coach_bookings', filter: `coach_id=eq.${id}` },
      { table: 'coach_hours',    filter: `coach_id=eq.${id}` },
      { table: 'coach_time_off', filter: `coach_id=eq.${id}` },
    ],
    q.refetch,
    !!id,
  );

  return q;
}

/**
 * Per-slot availability for a court on a given local date. Returns
 * one row per potential slot with `available: boolean` + `reason`
 * (`'Too soon' | 'Too far ahead' | 'Closed' | 'Blocked' | 'Booked' | null`).
 *
 * Backed by the `court_day_slots` RPC which evaluates hours, closures,
 * blocks and existing bookings server-side. Lets the slot picker
 * render unbookable slots disabled (with the reason) instead of the
 * customer clicking through to a 409 at checkout.
 *
 * `date` is a YYYY-MM-DD local date string; the RPC interprets it as
 * Asia/Dubai time. Re-fetches when the date changes or when bookings/
 * closures/blocks for the underlying court change in real time.
 */
export type CourtSlot = {
  start_at: string;
  end_at: string;
  available: boolean;
  reason: string | null;
};
export function useCourtDaySlots(
  courtId: string | undefined,
  date: string | undefined,
  tz: string = 'Asia/Dubai',
) {
  const q = useQuery(async () => {
    if (!courtId || !date) return { data: null, error: null };
    const { data, error } = await (supabase.rpc as any)('court_day_slots', {
      p_court_id: courtId,
      p_date: date,
      p_tz: tz,
    });
    return { data: (data ?? []) as CourtSlot[], error };
  }, [courtId, date, tz]);
  useRealtimeRefresh(
    `court-slots-${courtId}`,
    courtId ? [
      { table: 'court_bookings',            filter: `court_id=eq.${courtId}` },
      { table: 'court_availability_blocks', filter: `court_id=eq.${courtId}` },
    ] : [],
    q.refetch,
    !!courtId,
  );
  return q;
}

/** Opening hours for a club, flat array of { day_of_week, open_time, close_time }. */
export function useClubHours(clubId: string | undefined) {
  const q = useQuery(async () => {
    if (!clubId) return { data: null, error: null };
    return supabase
      .from('club_hours')
      .select('day_of_week, open_time, close_time')
      .eq('club_id', clubId);
  }, [clubId]);
  useRealtimeRefresh(
    `club-hours-${clubId}`,
    [
      { table: 'club_hours',    filter: `club_id=eq.${clubId}` },
      { table: 'club_closures', filter: `club_id=eq.${clubId}` },
    ],
    q.refetch,
    !!clubId,
  );
  return q;
}

/** Availability hours for a coach, flat array of { day_of_week, start_time, end_time }. */
export function useCoachHours(coachId: string | undefined) {
  const q = useQuery(async () => {
    if (!coachId) return { data: null, error: null };
    return supabase
      .from('coach_hours')
      .select('day_of_week, start_time, end_time')
      .eq('coach_id', coachId);
  }, [coachId]);
  useRealtimeRefresh(
    `coach-hours-${coachId}`,
    [
      { table: 'coach_hours',    filter: `coach_id=eq.${coachId}` },
      { table: 'coach_time_off', filter: `coach_id=eq.${coachId}` },
    ],
    q.refetch,
    !!coachId,
  );
  return q;
}

/**
 * Returns the set of clubs a given coach is actually allowed to teach at.
 * For staff coaches this is just their home club; for freelance coaches it
 * is the union of clubs with `accepts_freelance_coaches=true` and clubs with
 * an `approved` affiliation row. Returns clubs joined so the UI can show
 * name / address / photos in the picker.
 */
export function useCoachEligibleClubs(coachId: string | undefined) {
  return useQuery(async () => {
    if (!coachId) return { data: null, error: null };
    // coach_eligible_clubs is a view, so PostgREST can't infer a foreign-key
    // embed to clubs. Do it as two queries: first grab (club_id, source)
    // from the view, then fetch the full club rows by id.
    const pairsRes = await supabase
      .from('coach_eligible_clubs')
      .select('club_id, source')
      .eq('coach_id', coachId);
    if (pairsRes.error) return { data: null, error: pairsRes.error };
    const pairs = (pairsRes.data ?? []) as Array<{ club_id: string; source: string }>;
    if (pairs.length === 0) return { data: [], error: null };

    const ids = pairs.map((p) => p.club_id);
    const clubsRes = await supabase
      .from('clubs')
      .select('id, name, address, photos, logo_url, is_active')
      .in('id', ids)
      .eq('is_active', true);
    if (clubsRes.error) return { data: null, error: clubsRes.error };

    const sourceByClub = new Map(pairs.map((p) => [p.club_id, p.source] as const));
    const merged = (clubsRes.data ?? []).map((k: any) => ({
      ...k, source: sourceByClub.get(k.id) ?? 'open',
    }));
    return { data: merged, error: null };
  }, [coachId]);
}

export function useCoachBookings(userId?: string) {
  return useQuery(async () => {
    const uid = userId || useAuthStore.getState().user?.id;
    if (!uid) return { data: null, error: null };
    return supabase
      .from('coach_bookings')
      .select(`
        id, booking_reference, scheduled_at, ends_at, lesson_type, environment,
        session_minutes, participants, status, total_amount, currency,
        coaches(id, display_name, photo_url),
        clubs(id, name, address)
      `)
      .eq('user_id', uid)
      .order('scheduled_at', { ascending: false });
  }, [userId]);
}

export function useCoachPackages(coachId: string | undefined) {
  return useQuery(async () => {
    if (!coachId) return { data: null, error: null };
    return supabase
      .from('session_packages')
      .select('id, name, lesson_type, environment, session_minutes, sessions_included, price, currency, validity_weeks, transferable')
      .eq('coach_id', coachId)
      .eq('is_active', true)
      .order('sessions_included');
  }, [coachId]);
}

export function useMySessionCredits() {
  const userId = useAuthStore((s) => s.user?.id);
  return useQuery(async () => {
    if (!userId) return { data: null, error: null };
    return supabase
      .from('session_credits')
      .select(`
        id, sessions_purchased, sessions_remaining, expires_at, purchased_at, currency,
        session_packages(id, name, coach_id, lesson_type, environment, session_minutes,
          coaches(id, display_name, photo_url))
      `)
      .eq('user_id', userId)
      .gt('sessions_remaining', 0)
      .order('purchased_at', { ascending: false });
  }, [userId]);
}

export function usePurchaseSessionPackage() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const purchase = async (packageId: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase.functions.invoke('purchase-session-package', {
        body: { packageId },
      });
      if (error) throw error;
      setIsLoading(false);
      return { data, error: null };
    } catch (err: any) {
      setError(err);
      setIsLoading(false);
      return { data: null, error: err };
    }
  };

  return { purchase, isLoading, error };
}

// Cancellations + package transfer -----------------------------------------
export function useCancelCourtBooking() {
  const [isLoading, setIsLoading] = useState(false);
  const cancel = async (bookingId: string, reason?: string) => {
    setIsLoading(true);
    const { data, error } = await supabase.functions.invoke('cancel-court-booking', {
      body: { bookingId, reason },
    });
    setIsLoading(false);
    return { data, error };
  };
  return { cancel, isLoading };
}

export function useCancelCoachBooking() {
  const [isLoading, setIsLoading] = useState(false);
  const cancel = async (bookingId: string, reason?: string) => {
    setIsLoading(true);
    const { data, error } = await supabase.functions.invoke('cancel-coach-booking', {
      body: { bookingId, reason },
    });
    setIsLoading(false);
    return { data, error };
  };
  return { cancel, isLoading };
}

export function useTransferSessionCredit() {
  const [isLoading, setIsLoading] = useState(false);
  const transfer = async (creditId: string, recipientEmail: string) => {
    setIsLoading(true);
    const { data, error } = await supabase.functions.invoke('transfer-session-credit', {
      body: { creditId, recipientEmail },
    });
    setIsLoading(false);
    return { data, error };
  };
  return { transfer, isLoading };
}

// Reviews ------------------------------------------------------------------
export type ReviewTarget =
  | { coachBookingId: string; coachId: string }
  | { courtBookingId: string; courtId?: string; clubId?: string }
  | { bookingId: string; machineId: string };

export function useCoachReviews(coachId: string | undefined) {
  return useQuery(async () => {
    if (!coachId) return { data: null, error: null };
    return supabase
      .from('reviews')
      .select('id, rating, comment, created_at, status, user_id, profiles(full_name)')
      .eq('coach_id', coachId)
      .eq('status', 'approved')
      .order('created_at', { ascending: false })
      .limit(20);
  }, [coachId]);
}

export function useCourtReviews(courtId: string | undefined) {
  return useQuery(async () => {
    if (!courtId) return { data: null, error: null };
    return supabase
      .from('reviews')
      .select('id, rating, comment, created_at, user_id, profiles(full_name)')
      .eq('court_id', courtId)
      .eq('status', 'approved')
      .order('created_at', { ascending: false })
      .limit(20);
  }, [courtId]);
}

export function useSubmitReview() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const submit = async (target: ReviewTarget, rating: number, comment: string | null) => {
    const userId = useAuthStore.getState().user?.id;
    if (!userId) return { data: null, error: new Error('Sign in to review') };
    setIsLoading(true);
    setError(null);
    const row: Tables['reviews']['Insert'] = { user_id: userId, rating, comment };
    if ('coachBookingId' in target) {
      row.coach_booking_id = target.coachBookingId;
      row.coach_id = target.coachId;
    } else if ('courtBookingId' in target) {
      row.court_booking_id = target.courtBookingId;
      if (target.courtId) row.court_id = target.courtId;
      if (target.clubId) row.club_id = target.clubId;
    } else {
      row.booking_id = target.bookingId;
      row.machine_id = target.machineId;
    }
    const { data, error } = await supabase.from('reviews').insert([row]).select().single();
    setIsLoading(false);
    if (error) { setError(error); return { data: null, error }; }
    return { data, error: null };
  };

  return { submit, isLoading, error };
}

export type RentalItem = {
  id: string;
  name: string;
  description: string | null;
  price_unit: 'per_hour' | 'per_piece' | 'per_can';
  unit_price: number;
  currency: string;
  icon: string | null;
  max_quantity: number | null;
};

export function useRentalItems(clubId: string | undefined) {
  return useQuery(async () => {
    if (!clubId) return { data: null, error: null };
    return supabase
      .from('rental_items')
      .select('id, name, description, price_unit, unit_price, currency, icon, max_quantity')
      .eq('club_id', clubId)
      .eq('is_active', true)
      .order('name');
  }, [clubId]);
}

export function useCourtsAtClub(clubId: string | undefined, environment?: 'indoor' | 'outdoor') {
  return useQuery(async () => {
    if (!clubId) return { data: null, error: null };
    let q = supabase
      .from('courts')
      .select('id, name, surface, is_indoor, has_lights, hourly_rate_offpeak, hourly_rate_peak, currency')
      .eq('club_id', clubId)
      .eq('is_active', true);
    if (environment === 'indoor') q = q.eq('is_indoor', true);
    if (environment === 'outdoor') q = q.eq('is_indoor', false);
    return q.order('name');
  }, [clubId, environment]);
}

export function useCreateBundledBooking() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createBundle = async (payload: {
    coachId: string;
    courtId: string;
    lessonType: 'private' | 'semi_private' | 'group';
    environment: 'indoor' | 'outdoor';
    sessionMinutes: number;
    scheduledAt: string;
    participants?: number;
    notes?: string;
  }) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await invokeFunction<any>('create-bundled-booking', payload);
      setIsLoading(false);
      return { data, error: null };
    } catch (err: any) {
      setError(err);
      setIsLoading(false);
      return { data: null, error: err };
    }
  };

  return { createBundle, isLoading, error };
}

export function useCreateCoachBooking() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createCoachBooking = async (payload: {
    coachId: string;
    lessonType: 'private' | 'semi_private' | 'group';
    environment: 'indoor' | 'outdoor';
    sessionMinutes: number;
    scheduledAt: string;
    participants?: number;
    notes?: string;
    sessionCreditId?: string;
    clubId?: string;
    promoCode?: string;
  }) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await invokeFunction<any>('create-coach-booking', payload);
      setIsLoading(false);
      return { data, error: null };
    } catch (err: any) {
      setError(err);
      setIsLoading(false);
      return { data: null, error: err };
    }
  };

  return { createCoachBooking, isLoading, error };
}

export async function calculateCourtPrice(payload: {
  courtId: string;
  startAt: string;
  endAt: string;
}) {
  const { data, error } = await supabase.functions.invoke('calculate-court-price', {
    body: payload,
  });
  if (error) throw error;
  return data as {
    subtotal: number;
    platformFee: number;
    clubPayoutAmount: number;
    currency: string;
    total: number;
    peakMinutes: number;
    offpeakMinutes: number;
    clubId: string;
  };
}

// Add-ons API
export function useAddOns() {
  return useQuery(async () => {
    return supabase.from('add_ons').select('*').eq('is_active', true);
  });
}

// Bookings API
export function useBookings(userId?: string) {
  return useQuery(async () => {
    const user = userId || useAuthStore.getState().user?.id;
    if (!user) return { data: null, error: null };

    return supabase
      .from('bookings')
      .select(`
        *,
        machines(*),
        rental_packages(*),
        customer_addresses(*),
        booking_add_ons(*, add_ons(*))
      `)
      .eq('user_id', user)
      .order('created_at', { ascending: false });
  }, [userId]);
}

export function useBooking(id: string) {
  return useQuery(
    async () => {
      return supabase
        .from('bookings')
        .select(`
          *,
          machines(*),
          rental_packages(*),
          customer_addresses(*),
          booking_add_ons(*, add_ons(*)),
          payments(*)
        `)
        .eq('id', id)
        .single();
    },
    [id]
  );
}

// Reviews API
export function useReviews(machineId?: string) {
  return useQuery(async () => {
    let query = supabase
      .from('reviews')
      .select('*, profiles(full_name)')
      .eq('status', 'approved');

    if (machineId) {
      query = query.eq('machine_id', machineId);
    }

    return query.order('created_at', { ascending: false });
  }, [machineId]);
}

// FAQ API
export function useFAQs(category?: string) {
  return useQuery(async () => {
    let query = supabase.from('faq_items').select('*').eq('is_active', true);

    if (category) {
      query = query.eq('category', category);
    }

    return query.order('sort_order');
  }, [category]);
}

// Support Tickets API
export function useSupportTickets(userId?: string) {
  return useQuery(async () => {
    const user = userId || useAuthStore.getState().user?.id;
    if (!user) return { data: null, error: null };

    return supabase
      .from('support_tickets')
      .select('*, bookings(booking_reference)')
      .eq('user_id', user)
      .order('created_at', { ascending: false });
  }, [userId]);
}

// Customer Addresses API
export function useAddresses() {
  const userId = useAuthStore((state) => state.user?.id);

  return useQuery(async () => {
    if (!userId) return { data: null, error: null };

    return supabase
      .from('customer_addresses')
      .select('*, delivery_zones(*)')
      .eq('user_id', userId)
      .order('is_default', { ascending: false });
  }, [userId]);
}

async function invokeFunction<T>(name: string, body: unknown): Promise<T> {
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (error) throw await normalizeFunctionError(error);
  return data as T;
}

async function normalizeFunctionError(error: unknown, fallback = 'Request failed'): Promise<Error> {
  if (error instanceof Error && !(error as any)?.context) {
    return error;
  }

  const response = (error as any)?.context;
  if (response instanceof Response) {
    try {
      const body = await response.clone().json();
      const message = extractFunctionErrorMessage(body);
      if (message) return new Error(message);
    } catch {
      try {
        const text = await response.clone().text();
        const message = extractFunctionErrorMessage(text);
        if (message) return new Error(message);
      } catch {
        // Fall through to the generic error message below.
      }
    }
  }

  const message = extractFunctionErrorMessage(error)
    || (error as Error | undefined)?.message
    || fallback;
  return new Error(message);
}

function extractFunctionErrorMessage(value: unknown): string | null {
  if (!value) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (typeof value !== 'object') return null;

  const candidate = value as {
    error?: unknown;
    reason?: unknown;
    message?: unknown;
    details?: unknown;
  };

  const errorMessage = extractFunctionErrorMessage(candidate.error);
  const reasonMessage = extractFunctionErrorMessage(candidate.reason);
  if (reasonMessage && (!errorMessage || /not available/i.test(errorMessage))) {
    return reasonMessage;
  }

  return errorMessage
    || reasonMessage
    || extractFunctionErrorMessage(candidate.message)
    || extractFunctionErrorMessage(candidate.details);
}

// Mutation hooks
export function useCreateBooking() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createBooking = async (bookingData: any) => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await invokeFunction<any>('create-booking', bookingData);
      setIsLoading(false);
      return { data, error: null };
    } catch (err: any) {
      setError(err);
      setIsLoading(false);
      return { data: null, error: err };
    }
  };

  return { createBooking, isLoading, error };
}

// AI-assisted identity verification.
// User uploads Emirates ID front/back + selfie to Supabase Storage, then
// calls the submit edge function which runs an OpenAI vision check.
// If confidence is high enough the record is auto-approved; otherwise it
// goes to ops review.
export function useIdentityVerification() {
  const userId = useAuthStore((s) => s.user?.id);
  const [isVerified, setIsVerified] = useState<boolean | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refresh = async () => {
    if (!userId) {
      setIsVerified(null);
      setIsAdmin(false);
      return null;
    }
    // maybeSingle() so a transient 0-row response (e.g. auth JWT not yet
    // attached, or a freshly-deleted profile) returns data=null instead
    // of a 406 "cannot be satisfied" error.
    const { data } = await supabase
      .from('profiles')
      .select('identity_verified_at, is_admin')
      .eq('user_id', userId)
      .maybeSingle();
    const verified = !!data?.identity_verified_at;
    setIsVerified(verified);
    setIsAdmin(!!data?.is_admin);
    return verified;
  };

  useEffect(() => { refresh(); }, [userId]);

  const uploadOne = async (file: File, kind: 'id-front' | 'id-back' | 'selfie') => {
    if (!userId) throw new Error('Not signed in');
    const ext = (file.name.split('.').pop() ?? 'jpg').toLowerCase();
    const path = `${userId}/${kind}-${Date.now()}.${ext}`;
    const { error: upErr } = await supabase.storage
      .from('identity-documents')
      .upload(path, file, { upsert: true, contentType: file.type });
    if (upErr) throw upErr;
    return path;
  };

  const startVerification = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase.functions.invoke('create-identity-session');
      if (error) throw error;

      if (data?.alreadyVerified) {
        await refresh();
        return { verified: true, pending: false, clientSecret: null, sessionId: null };
      }

      return {
        verified: false,
        pending: !!data?.clientSecret,
        clientSecret: data?.clientSecret ?? null,
        sessionId: data?.sessionId ?? null,
      };
    } catch (err: any) {
      const nextError = err instanceof Error ? err : new Error(String(err));
      setError(nextError);
      return { verified: false, pending: false, error: nextError };
    } finally {
      setIsLoading(false);
    }
  };

  const submitVerification = async (files: {
    idFront: File;
    idBack: File;
    selfie: File;
  }) => {
    setIsLoading(true);
    setError(null);
    try {
      const [idFrontPath, idBackPath, selfiePath] = await Promise.all([
        uploadOne(files.idFront, 'id-front'),
        uploadOne(files.idBack, 'id-back'),
        uploadOne(files.selfie, 'selfie'),
      ]);

      const { data, error } = await supabase.functions.invoke(
        'submit-identity-verification',
        { body: { idFrontPath, idBackPath, selfiePath } }
      );
      if (error) throw error;

      if (data?.alreadyVerified) {
        await refresh();
        return { status: 'verified' as const, verified: true };
      }

      if (data?.status === 'verified') {
        await refresh();
        return {
          status: 'verified' as const,
          verified: true,
          confidence: data.confidence,
          reasoning: data.reasoning,
        };
      }

      return {
        status: 'pending_review' as const,
        verified: false,
        confidence: data?.confidence,
        reasoning: data?.reasoning,
      };
    } catch (err: any) {
      setError(err);
      return { status: 'error' as const, verified: false, error: err };
    } finally {
      setIsLoading(false);
    }
  };

  return { isVerified, isAdmin, isLoading, error, startVerification, submitVerification, refresh };
}

// Payment records are created server-side by create-booking / extend-booking.
// Clients only confirm the PaymentIntent they received.
export function useConfirmPayment() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const confirmPayment = async (payload: { bookingId: string; paymentIntentId: string }) => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await invokeFunction<any>('confirm-payment-and-booking', payload);
      setIsLoading(false);
      return { data, error: null };
    } catch (err: any) {
      setError(err);
      setIsLoading(false);
      return { data: null, error: err };
    }
  };

  return { confirmPayment, isLoading, error };
}

export function useCreateReview() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createReview = async (reviewData: any) => {
    setIsLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase
        .from('reviews')
        .insert([reviewData])
        .select()
        .single();

      if (error) throw error;

      setIsLoading(false);
      return { data, error: null };
    } catch (err: any) {
      setError(err);
      setIsLoading(false);
      return { data: null, error: err };
    }
  };

  return { createReview, isLoading, error };
}

export function useCreateSupportTicket() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createTicket = async (ticketData: any) => {
    setIsLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase
        .from('support_tickets')
        .insert([ticketData])
        .select()
        .single();

      if (error) throw error;

      setIsLoading(false);
      return { data, error: null };
    } catch (err: any) {
      setError(err);
      setIsLoading(false);
      return { data: null, error: err };
    }
  };

  return { createTicket, isLoading, error };
}

// Favorites API --------------------------------------------------------------
export type FavoriteTarget =
  | { kind: 'coach'; id: string }
  | { kind: 'court'; id: string }
  | { kind: 'club';  id: string };

type FavoriteRow = {
  id: string;
  coach_id: string | null;
  court_id: string | null;
  club_id:  string | null;
  created_at: string;
  coaches?: any;
  courts?:  any;
  clubs?:   any;
};

/**
 * Live snapshot of the signed-in user's favourites plus the embedded
 * target details. useFavorites().data is an array of FavoriteRow.
 * Unauthenticated users get an empty list — no error.
 */
export function useFavorites() {
  const userId = useAuthStore((s) => s.user?.id);
  return useQuery(async () => {
    if (!userId) return { data: [], error: null };
    return supabase
      .from('favorites')
      .select(`
        id, coach_id, court_id, club_id, created_at,
        coaches(id, display_name, bio, photo_url, type, years_experience),
        courts(id, name, surface, is_indoor, has_lights, hourly_rate_offpeak,
               currency, photos, club_id, clubs(id, name, address)),
        clubs(id, name, address, photos, logo_url, description, accepts_freelance_coaches)
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
  }, [userId]);
}

/**
 * Lightweight lookup of only the ids a user has starred, used to render
 * the filled/outline state on list cards without pulling embedded data.
 */
export function useFavoriteIds() {
  const userId = useAuthStore((s) => s.user?.id);
  return useQuery(async () => {
    if (!userId) return { data: { coach: [], court: [], club: [] }, error: null };
    const { data, error } = await supabase
      .from('favorites')
      .select('coach_id, court_id, club_id')
      .eq('user_id', userId);
    if (error) return { data: null, error };
    const set = {
      coach: new Set<string>(),
      court: new Set<string>(),
      club:  new Set<string>(),
    };
    for (const r of (data ?? []) as any[]) {
      if (r.coach_id) set.coach.add(r.coach_id);
      if (r.court_id) set.court.add(r.court_id);
      if (r.club_id)  set.club.add(r.club_id);
    }
    return {
      data: {
        coach: Array.from(set.coach),
        court: Array.from(set.court),
        club:  Array.from(set.club),
      },
      error: null,
    };
  }, [userId]);
}

/** Hook that returns an async toggle() for adding/removing a favourite. */
export function useToggleFavorite() {
  const userId = useAuthStore((s) => s.user?.id);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const toggle = async (target: FavoriteTarget, currentlyFavourited: boolean) => {
    if (!userId) {
      const err = new Error('Sign in to favourite things.');
      setError(err);
      return { data: null, error: err };
    }
    setIsLoading(true);
    setError(null);

    try {
      const column =
        target.kind === 'coach' ? 'coach_id' :
        target.kind === 'court' ? 'court_id' : 'club_id';

      if (currentlyFavourited) {
        const { error } = await supabase
          .from('favorites')
          .delete()
          .eq('user_id', userId)
          .eq(column, target.id);
        if (error) throw error;
      } else {
          const row: Tables['favorites']['Insert'] =
            target.kind === 'coach'
              ? { user_id: userId, coach_id: target.id }
              : target.kind === 'court'
                ? { user_id: userId, court_id: target.id }
                : { user_id: userId, club_id: target.id };
        const { error } = await supabase.from('favorites').insert(row);
        if (error && !(error as any).message?.includes('duplicate')) throw error;
      }
      setIsLoading(false);
      return { data: { favourited: !currentlyFavourited }, error: null };
    } catch (err: any) {
      setError(err);
      setIsLoading(false);
      return { data: null, error: err };
    }
  };

  return { toggle, isLoading, error };
}
